// Helpers for the direct-to-storage presigned PUT. The URL is signed against exact
// header values, so anything that feeds those headers has to be reproduced
// byte-for-byte by the server (HasMediaPipeline) and here.

const DEFAULT_MIN_REMAINING_MS = 60 * 1000;

// sync with app/models/concerns/has_media_pipeline.rb (safe_for_inline_display?)
export function isSafeForInlineDisplay(contentType) {
  if (!contentType) return false;

  const contentTypeLower = contentType.toLowerCase();
  if (contentTypeLower.startsWith('image/svg')) return false;

  const safePrefixes = ['image/', 'video/', 'audio/'];
  if (safePrefixes.some((prefix) => contentTypeLower.startsWith(prefix))) return true;

  const safeTypes = ['application/pdf', 'text/markdown', 'text/plain', 'text/x-markdown'];
  return safeTypes.includes(contentTypeLower);
}

// Filename for the signed Content-Disposition. Printable ASCII only: the browser
// sends header values as ISO-8859-1 bytes while the server signs UTF-8, so any
// non-ASCII character would sign differently from what the client sends.
// sync with app/models/concerns/has_media_pipeline.rb (content_disposition_file_name)
export function contentDispositionFileName(fileName) {
  const sanitized = String(fileName ?? '')
    .replace(/[^ -~]/gu, '_')
    .replace(/["\\]/g, '_')
    .trim();
  return sanitized || 'Untitled';
}

// The exact header the presigned PUT is signed with.
// sync with app/models/concerns/has_media_pipeline.rb (content_disposition_for)
export function contentDispositionHeader(contentType, fileName) {
  const disposition = isSafeForInlineDisplay(contentType) ? 'inline' : 'attachment';
  return `${disposition};filename="${contentDispositionFileName(fileName)}"`;
}

function numericParam(params, name) {
  const value = params.get(name);
  if (value === null || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// Expiry baked into a SigV4 (X-Amz-Date + X-Amz-Expires) or legacy (Expires) presigned
// URL, as epoch ms. Null when the URL carries neither in full.
export function presignedUrlExpiresAt(url) {
  let params;
  try {
    params = new URL(url).searchParams;
  } catch {
    return null;
  }

  const amzDate = params.get('X-Amz-Date');
  const amzExpires = numericParam(params, 'X-Amz-Expires');
  const dateMatch = amzDate ? /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(amzDate) : null;
  if (dateMatch && amzExpires !== null) {
    const [, year, month, day, hour, minute, second] = dateMatch.map(Number);
    return Date.UTC(year, month - 1, day, hour, minute, second) + amzExpires * 1000;
  }

  const legacyExpires = numericParam(params, 'Expires');
  if (legacyExpires !== null) return legacyExpires * 1000;

  return null;
}

// A URL with unknown expiry is still attempted; a rejected PUT gets re-signed downstream.
export function isPresignedUrlUsable(url, { now = Date.now(), minRemainingMs = DEFAULT_MIN_REMAINING_MS } = {}) {
  const expiresAt = presignedUrlExpiresAt(url);
  if (expiresAt === null) return true;
  return expiresAt - now > minRemainingMs;
}
